import { Component, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import mermaid from 'mermaid';

@Component({
    selector: 'shared-security-flow-dialog',
    imports: [CommonModule, MatDialogModule, MatIconModule, MatButtonModule, MatTooltipModule],
    template: `
        <div class="dialog-container">
            <!-- Header -->
            <div class="dialog-header">
                <div class="header-left">
                    <mat-icon class="header-icon">security</mat-icon>
                    <h2>How the Security System Works</h2>
                </div>
                <div class="header-actions">
                    <button
                        mat-icon-button
                        (click)="activeTab = 'explanation'"
                        [class.active]="activeTab === 'explanation'"
                        matTooltip="Step-by-step explanation"
                    >
                        <mat-icon>article</mat-icon>
                    </button>
                    <button
                        mat-icon-button
                        (click)="switchToDiagram()"
                        [class.active]="activeTab === 'diagram'"
                        matTooltip="Visual flow diagram"
                    >
                        <mat-icon>account_tree</mat-icon>
                    </button>
                    <button mat-icon-button (click)="close()" matTooltip="Close">
                        <mat-icon>close</mat-icon>
                    </button>
                </div>
            </div>

            <!-- Explanation Tab -->
            <div class="content explanation-content" [class.tab-hidden]="activeTab !== 'explanation'">
                <div class="flow-steps">
                    <div class="step">
                        <div class="step-number">1</div>
                        <div class="step-content">
                            <h3><mat-icon>login</mat-icon> User Logs In</h3>
                            <p>
                                A user signs in using <strong>Google</strong>, <strong>Microsoft</strong>, or
                                <strong>email &amp; password</strong>. Firebase Authentication verifies their identity
                                and creates a session.
                            </p>
                        </div>
                    </div>

                    <div class="step-arrow"><mat-icon>arrow_downward</mat-icon></div>

                    <div class="step">
                        <div class="step-number">2</div>
                        <div class="step-content">
                            <h3><mat-icon>person</mat-icon> User Profile Loads</h3>
                            <p>
                                The system loads the user's profile from the database. This includes which
                                <strong>organization</strong> they belong to and what <strong>features</strong> and
                                <strong>permissions</strong> they've been assigned.
                            </p>
                        </div>
                    </div>

                    <div class="step-arrow"><mat-icon>arrow_downward</mat-icon></div>

                    <div class="step highlight">
                        <div class="step-number">3</div>
                        <div class="step-content">
                            <h3><mat-icon>groups</mat-icon> Security Groups &rarr; Features &rarr; Permissions</h3>
                            <p>
                                This is the core of the system. An admin assigns users to
                                <strong>Security Groups</strong>. Each group grants access to specific
                                <strong>Features</strong> (like "Organization Settings" or "Workspace"). Each feature
                                has granular <strong>Permissions</strong> (like "can edit", "can delete", "can view").
                            </p>
                            <div class="example-box">
                                <strong>Example:</strong> User "Jane" is in the "Managers" security group, which
                                includes the "organization-settings" feature with "read" and "write" permissions.
                            </div>
                        </div>
                    </div>

                    <div class="step-arrow"><mat-icon>arrow_downward</mat-icon></div>

                    <div class="step">
                        <div class="step-number">4</div>
                        <div class="step-content">
                            <h3><mat-icon>route</mat-icon> Routes Map Features to Pages</h3>
                            <p>
                                <strong>Route documents</strong> connect URL paths to features. Each route says "to
                                access this page, you need this feature." For example, the route
                                <code>/app/organization/settings</code> requires the "organization-settings" feature.
                            </p>
                            <p>There are three types of routes:</p>
                            <ul style="margin: 4px 0; padding-left: 20px;">
                                <li><strong>User routes</strong> — require a specific feature to access.</li>
                                <li><strong>System routes</strong> — inherit access from a parent route hierarchy.</li>
                                <li>
                                    <strong>Public routes</strong> — accessible to <em>any authenticated user</em>, no
                                    feature required (e.g. <code>/app/notifications</code>).
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="step-arrow"><mat-icon>arrow_downward</mat-icon></div>

                    <div class="step">
                        <div class="step-number">5</div>
                        <div class="step-content">
                            <h3><mat-icon>menu</mat-icon> Menu Buttons Appear</h3>
                            <p>
                                The sidebar menu is built by matching the user's features against route documents. A
                                menu item only appears if the user has a feature that matches a route for that page.
                                <strong>No feature = no menu button.</strong>
                            </p>
                        </div>
                    </div>

                    <div class="step-arrow"><mat-icon>arrow_downward</mat-icon></div>

                    <div class="step">
                        <div class="step-number">6</div>
                        <div class="step-content">
                            <h3><mat-icon>shield</mat-icon> Route Guard Checks Access</h3>
                            <p>
                                When a user clicks a menu button or navigates to a URL, the
                                <strong>Route Guard</strong> checks:
                            </p>
                            <ol>
                                <li>Is the user <strong>logged in</strong>? If not &rarr; redirect to login.</li>
                                <li>
                                    Is it a <strong>public route</strong>? If yes &rarr; allow access (any authenticated
                                    user).
                                </li>
                                <li>
                                    Does the user have the <strong>required feature</strong>? If yes &rarr; allow
                                    access.
                                </li>
                                <li>
                                    If not, is it a <strong>system route</strong>? Check the parent route's features
                                    against the user's features.
                                </li>
                                <li>If all checks fail &rarr; <strong>block access</strong>.</li>
                            </ol>
                        </div>
                    </div>

                    <div class="step-arrow"><mat-icon>arrow_downward</mat-icon></div>

                    <div class="step">
                        <div class="step-number">7</div>
                        <div class="step-content">
                            <h3><mat-icon>tune</mat-icon> Permissions Control What You Can Do</h3>
                            <p>
                                Once on a page, individual <strong>permissions</strong> control what actions are
                                available. For example, a user might see a settings page (feature access) but the "Save"
                                button is only visible if they have the "write" permission on that feature.
                            </p>
                            <div class="example-box">
                                <strong>In summary:</strong> Features control <em>which pages you see</em>. Permissions
                                control <em>what you can do</em> on those pages.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Diagram Tab -->
            <div class="content diagram-content" [class.tab-hidden]="activeTab !== 'diagram'">
                <!-- Zoom toolbar -->
                <div class="zoom-toolbar" *ngIf="diagramRendered">
                    <button mat-icon-button (click)="zoomIn()" matTooltip="Zoom in">
                        <mat-icon>zoom_in</mat-icon>
                    </button>
                    <button mat-icon-button (click)="zoomOut()" matTooltip="Zoom out">
                        <mat-icon>zoom_out</mat-icon>
                    </button>
                    <button mat-icon-button (click)="fitToWidth()" matTooltip="Fit to width">
                        <mat-icon>fit_screen</mat-icon>
                    </button>
                    <button mat-icon-button (click)="resetZoom()" matTooltip="Reset (100%)">
                        <mat-icon>center_focus_strong</mat-icon>
                    </button>
                    <span class="zoom-label">{{ Math.round(zoomLevel * 100) }}%</span>
                </div>

                <div #diagramScrollArea class="diagram-scroll-area">
                    <div
                        #mermaidContainer
                        class="mermaid-container"
                        [style.transform]="'scale(' + zoomLevel + ')'"
                        [style.transformOrigin]="'top left'"
                    ></div>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            .dialog-container {
                display: flex;
                flex-direction: column;
                width: 100%;
                height: 100%;
                overflow: hidden;
                background: #1a1a2e;
                color: rgba(255, 255, 255, 0.87);
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                text-rendering: optimizeLegibility;
            }
            .dialog-container *,
            .dialog-container *::before,
            .dialog-container *::after {
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
            }
            .dialog-container strong,
            .dialog-container b {
                font-weight: 500;
            }

            .tab-hidden {
                display: none !important;
            }

            .dialog-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 16px 24px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                flex-shrink: 0;
            }
            .header-left {
                display: flex;
                align-items: center;
                gap: 12px;
                h2 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 400;
                    color: #e8eaf6;
                }
            }
            .header-icon {
                color: #80cbc4;
            }
            .header-actions {
                display: flex;
                gap: 4px;
                button.active {
                    color: #80cbc4;
                    background: rgba(128, 203, 196, 0.1);
                }
            }

            .content {
                flex: 1;
                overflow-y: auto;
                padding: 24px;
                &::-webkit-scrollbar {
                    width: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.12);
                    border-radius: 3px;
                }
            }

            /* --- Explanation tab --- */
            .explanation-content {
                overflow-y: auto;
            }
            .flow-steps {
                max-width: 700px;
                margin: 0 auto;
            }

            .step {
                display: flex;
                gap: 16px;
                padding: 16px;
                border-radius: 12px;
                background: rgba(255, 255, 255, 0.03);
                border: 1px solid rgba(255, 255, 255, 0.06);
                transition: background 0.2s;
            }
            .step:hover {
                background: rgba(255, 255, 255, 0.06);
            }
            .step.highlight {
                background: rgba(128, 203, 196, 0.06);
                border-color: rgba(128, 203, 196, 0.18);
            }

            .step-number {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: linear-gradient(135deg, #80cbc4, #4db6ac);
                color: #1a1a2e;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 600;
                font-size: 14px;
                flex-shrink: 0;
                margin-top: 2px;
            }

            .step-content {
                flex: 1;
                h3 {
                    margin: 0 0 8px 0;
                    font-size: 15px;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #e8eaf6;
                    mat-icon {
                        font-size: 20px;
                        width: 20px;
                        height: 20px;
                        color: #80cbc4;
                    }
                }
                p {
                    margin: 0 0 8px 0;
                    font-size: 13px;
                    line-height: 1.6;
                    color: rgba(255, 255, 255, 0.65);
                    strong {
                        color: #b2dfdb;
                    }
                }
                ol {
                    margin: 4px 0 0 0;
                    padding-left: 20px;
                    li {
                        font-size: 13px;
                        line-height: 1.6;
                        color: rgba(255, 255, 255, 0.65);
                        margin-bottom: 2px;
                        strong {
                            color: #b2dfdb;
                        }
                    }
                }
                code {
                    font-size: 12px;
                    padding: 2px 6px;
                    background: rgba(128, 203, 196, 0.1);
                    border-radius: 4px;
                    font-family: 'Roboto Mono', monospace;
                    color: #80cbc4;
                }
            }

            .step-arrow {
                display: flex;
                justify-content: center;
                padding: 4px 0;
                mat-icon {
                    color: rgba(255, 255, 255, 0.15);
                    font-size: 20px;
                    width: 20px;
                    height: 20px;
                }
            }

            .example-box {
                margin-top: 8px;
                padding: 10px 14px;
                background: rgba(128, 203, 196, 0.06);
                border-radius: 8px;
                font-size: 12px;
                line-height: 1.6;
                color: rgba(255, 255, 255, 0.65);
                border-left: 3px solid #80cbc4;
                strong {
                    color: #b2dfdb;
                }
                em {
                    color: #80cbc4;
                    font-style: italic;
                }
            }

            /* --- Diagram tab --- */
            .diagram-content {
                padding: 0;
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }

            .zoom-toolbar {
                display: flex;
                align-items: center;
                gap: 2px;
                padding: 4px 16px;
                border-bottom: 1px solid rgba(255, 255, 255, 0.06);
                flex-shrink: 0;
                background: rgba(255, 255, 255, 0.02);
                .zoom-label {
                    margin-left: 8px;
                    font-size: 12px;
                    color: rgba(255, 255, 255, 0.4);
                    min-width: 40px;
                }
            }

            .diagram-scroll-area {
                flex: 1;
                overflow: auto;
                padding: 16px;
                &::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.12);
                    border-radius: 3px;
                }
            }

            .mermaid-container {
                display: inline-block;
                transform-origin: top left;
            }
        `,
    ],
})
export class SecurityFlowDialogComponent {
    @ViewChild('mermaidContainer') mermaidContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('diagramScrollArea') diagramScrollArea!: ElementRef<HTMLDivElement>;

    activeTab: 'explanation' | 'diagram' = 'explanation';
    zoomLevel = 1;
    Math = Math; // expose to template

    diagramRendered = false;
    private renderCounter = 0;
    private svgNaturalWidth = 0;

    private readonly diagramMarkup = `
flowchart TB
    subgraph LOGIN ["1. LOGIN"]
        direction TB
        A1["User visits app"] --> A2["Login Page"]
        A2 --> A3{"Auth Provider"}
        A3 -->|Google| A4["Firebase Auth"]
        A3 -->|Microsoft| A4
        A3 -->|Email/Pass| A4
        A4 --> A5["Authenticated User Session"]
    end

    subgraph USERDATA ["2. USER PROFILE"]
        direction TB
        B1["Load User Document"] --> B2["Selected Organization"]
        B2 --> B3["User's Features List"]
        B2 --> B4["Feature Permissions Map"]
    end

    subgraph ORGSETUP ["3. ADMIN SETUP"]
        direction TB
        C1["Admin creates Security Groups"] --> C2["Each group has Features + Permissions"]
        C2 --> C3["Users assigned to groups"]
        C3 --> C4["All features & permissions aggregated to user profile"]
    end

    subgraph ROUTES ["4. ROUTE DOCUMENTS"]
        direction TB
        D1["Each page has a Route Document"] --> D2["Route = URL path + required feature"]
        D2 --> D3["System routes inherit from parent"]
        D2 --> D3b["Public routes: any authenticated user"]
    end

    subgraph MENU ["5. MENU GENERATION"]
        direction TB
        F1["Static menu structure"] --> F2["Filter by user's features and matching routes"]
        F2 --> F3{"Route doc exists AND user has feature?"}
        F3 -->|Yes| F4["Show menu button"]
        F3 -->|No| F5["Hide menu button"]
    end

    subgraph GUARD ["6. ROUTE GUARD"]
        direction TB
        G1["User navigates to page"] --> G2{"Logged in?"}
        G2 -->|No| G3["Redirect to login"]
        G2 -->|Yes| G3b{"Public route?"}
        G3b -->|Yes| G5["Allow access"]
        G3b -->|No| G4{"Has required feature?"}
        G4 -->|Yes| G5
        G4 -->|No| G6{"System route? Check parent"}
        G6 -->|Access via parent| G5
        G6 -->|No access| G7["Block - Forbidden"]
    end

    subgraph PERMS ["7. PERMISSIONS"]
        direction TB
        H1["On the page, check specific permissions"] --> H2{"Has permission?"}
        H2 -->|Yes| H3["Show button / Allow action"]
        H2 -->|No| H4["Hide button / Block action"]
    end

    LOGIN --> USERDATA
    ORGSETUP -.->|"writes features to"| USERDATA
    USERDATA --> MENU
    ROUTES --> MENU
    MENU --> GUARD
    GUARD --> PERMS

    style LOGIN fill:#1a237e,color:#fff,stroke:#3949ab
    style USERDATA fill:#004d40,color:#fff,stroke:#00897b
    style ORGSETUP fill:#4a148c,color:#fff,stroke:#7b1fa2
    style ROUTES fill:#b71c1c,color:#fff,stroke:#e53935
    style MENU fill:#1b5e20,color:#fff,stroke:#43a047
    style GUARD fill:#bf360c,color:#fff,stroke:#f4511e
    style PERMS fill:#0d47a1,color:#fff,stroke:#1976d2
    `;

    constructor(
        private dialogRef: MatDialogRef<SecurityFlowDialogComponent>,
        private cdr: ChangeDetectorRef
    ) {}

    private async renderDiagram(): Promise<void> {
        if (this.diagramRendered) return;

        // Small delay for DOM to be fully visible and laid out
        await new Promise((resolve) => setTimeout(resolve, 150));

        if (!this.mermaidContainer) return;

        this.diagramRendered = true;
        try {
            this.renderCounter++;
            const id = 'security-flow-' + this.renderCounter + '-' + Math.random().toString(36).slice(2, 6);

            mermaid.initialize({
                startOnLoad: false,
                theme: 'dark',
                flowchart: {
                    useMaxWidth: false,
                    htmlLabels: true,
                    curve: 'basis',
                },
                securityLevel: 'loose',
            });

            const { svg } = await mermaid.render(id, this.diagramMarkup);
            this.mermaidContainer.nativeElement.innerHTML = svg;

            // Read the SVG's natural width for fit-to-width calculations
            const svgEl = this.mermaidContainer.nativeElement.querySelector('svg');
            if (svgEl) {
                this.svgNaturalWidth = svgEl.getBoundingClientRect().width || svgEl.clientWidth || 800;
            }

            this.cdr.detectChanges();

            // Auto-fit to container width after a frame so layout is settled
            await new Promise((resolve) => setTimeout(resolve, 50));
            this.fitToWidth();
            this.cdr.detectChanges();
        } catch (err) {
            console.error('Failed to render Mermaid diagram:', err);
            this.mermaidContainer.nativeElement.innerHTML =
                '<p style="color: red; padding: 24px;">Failed to render diagram. Check console for details.</p>';
        }
    }

    switchToDiagram(): void {
        this.activeTab = 'diagram';
        // Render on first switch — tab must be visible for measurements to work
        if (!this.diagramRendered) {
            this.cdr.detectChanges(); // Make tab visible first
            this.renderDiagram();
        }
    }

    zoomIn(): void {
        this.zoomLevel = Math.min(3, this.zoomLevel + 0.15);
    }

    zoomOut(): void {
        this.zoomLevel = Math.max(0.2, this.zoomLevel - 0.15);
    }

    fitToWidth(): void {
        if (!this.diagramScrollArea || !this.svgNaturalWidth) return;
        const containerWidth = this.diagramScrollArea.nativeElement.clientWidth - 32; // minus padding
        this.zoomLevel = containerWidth / this.svgNaturalWidth;
    }

    resetZoom(): void {
        this.zoomLevel = 1;
    }

    close(): void {
        this.dialogRef.close();
    }
}
